// Browser-side solver for a self-hosted ALTCHA proof-of-work challenge.
//
// The server (altcha-lib v1) issues a challenge of the form
//   { algorithm: 'SHA-256', challenge: <hex>, salt: <string>, signature, maxnumber }
// where `challenge` is SHA-256(salt + number) for some number in [0, maxnumber].
// Solving means finding that number by brute force. This is intentionally the
// only "work" — invisible to a real visitor (sub-second) but a real cost to a
// bot submitting at volume. We compute it with the built-in Web Crypto API, so
// the client carries no extra dependency; the server does the security-critical
// create/verify with the vetted library.

const encoder = new TextEncoder()

function toHex (buffer) {
  const bytes = new Uint8Array(buffer)
  let hex = ''
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0')
  }
  return hex
}

/**
 * Find the number whose SHA-256(salt + number) equals `challenge`.
 *
 * @param {{algorithm?:string, challenge:string, salt:string, maxnumber?:number}} c
 * @returns {Promise<number|null>} the solving number, or null if none found
 *   within `maxnumber` (or if Web Crypto is unavailable).
 */
export async function solveChallenge (c) {
  if (!c || typeof c.challenge !== 'string' || typeof c.salt !== 'string') {
    return null
  }
  if (!(window.crypto && window.crypto.subtle)) {
    // No SubtleCrypto (very old browser or insecure context). The submission
    // will proceed without a solution and be rejected server-side.
    return null
  }

  const max = Number.isFinite(c.maxnumber) ? c.maxnumber : 1000000
  for (let number = 0; number <= max; number++) {
    const digest = await window.crypto.subtle.digest(
      'SHA-256',
      encoder.encode(c.salt + number)
    )
    if (toHex(digest) === c.challenge) {
      return number
    }
  }
  return null
}

/**
 * Solve a challenge and encode the solution payload the server expects
 * (base64 of the challenge fields plus the found number).
 *
 * @param {object} challenge the object returned by GET /api/public/altcha-challenge
 * @returns {Promise<string|null>} base64 solution payload, or null if unsolved
 */
export async function solveToPayload (challenge) {
  const number = await solveChallenge(challenge)
  if (number === null) return null
  const payload = {
    algorithm: challenge.algorithm,
    challenge: challenge.challenge,
    number,
    salt: challenge.salt,
    signature: challenge.signature
  }
  // btoa over the JSON — matches Buffer.from(json).toString('base64') server-side.
  return btoa(JSON.stringify(payload))
}

export default solveToPayload
